'use client';

import { useEventTranslation } from '@/components/EventTranslatedContent';
import { ShareExportToolbar } from '@/components/features/ShareExportToolbar';
import { BookmarkButton } from '@/components/features/BookmarkButton';
import { ReportButton } from '@/components/feedback/ReportButton';
import { EventMapImage } from '@/components/EventMapImage';

/** Conditional subtitle displaying titleLocal */
export function TranslatedSubtitle() {
  const t = useEventTranslation();
  if (!t.titleLocal) return null;
  return <p className="mt-1 text-lg text-text-secondary">{t.titleLocal}</p>;
}

/** Unicode-aware text truncation using Intl.Segmenter where available */
function truncateText(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  // Use Intl.Segmenter for word-boundary aware truncation (works for CJK, Arabic, Thai, etc.)
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const SegmenterCtor = (Intl as unknown as {
      Segmenter: new (
        locales?: string,
        options?: { granularity: 'word' | 'sentence' | 'grapheme' },
      ) => { segment(input: string): Iterable<{ segment: string }> };
    }).Segmenter;
    const segmenter = new SegmenterCtor(undefined, { granularity: 'word' });
    let result = '';
    for (const { segment } of segmenter.segment(text)) {
      if (result.length + segment.length > maxChars) break;
      result += segment;
    }
    return result.trimEnd();
  }
  // Fallback: slice at char boundary (safe for all scripts, just may cut mid-word for Latin)
  return text.slice(0, maxChars).replace(/\s+\S*$/, '');
}

/** Conditional notes section */
export function TranslatedNotes({ heading, readMoreLabel }: { heading: string; readMoreLabel?: string }) {
  const t = useEventTranslation();
  if (!t.notes) return null;
  const isLong = t.notes.length > 300;
  const expandLabel = readMoreLabel || 'read more';
  return (
    <div className="rounded-xl border border-border-subtle bg-surface-card p-5 mb-8">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-text-secondary">
        {heading}
      </h2>
      {isLong ? (
        <details>
          <summary className="cursor-pointer text-sm text-brand hover:underline" aria-label={`${heading} — ${expandLabel}`}>
            {truncateText(t.notes, 150)}…
            <span className="ms-1 text-xs text-text-dim">({expandLabel})</span>
          </summary>
          <p className="mt-2 text-sm text-text-primary leading-relaxed">{t.notes}</p>
        </details>
      ) : (
        <p className="text-sm text-text-primary leading-relaxed">{t.notes}</p>
      )}
    </div>
  );
}

/** Action buttons that need translated title/subtitle */
export function EventActions({
  eventSlug,
  eventId,
  eventDateStart,
  eventCountry,
}: {
  eventSlug: string;
  eventId: string;
  eventDateStart: string;
  eventCountry: string;
}) {
  const t = useEventTranslation();
  return (
    <div className="mb-6 flex items-center gap-3">
      <ShareExportToolbar title={t.title} subtitle={t.titleLocal} />
      <BookmarkButton pageSlug={eventSlug} pageTitle={t.title} eventDateStart={eventDateStart} eventCountry={eventCountry} />
      <ReportButton pageSlug={eventSlug} entityId={eventId} pageTitle={t.title} pageCountry={eventCountry} pageType="event" />
    </div>
  );
}

/** Event map with translated title */
export function TranslatedEventMap({ eventGroup, lang }: { eventGroup: string; lang?: string }) {
  const t = useEventTranslation();
  return <EventMapImage eventGroup={eventGroup} title={t.title} lang={lang} />;
}
